import { useMemo, useState } from 'react';

import CallMergeIcon from '@mui/icons-material/CallMerge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { isAxiosError } from 'axios';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import ListFilterEmptyState from 'src/common/components/dashboard/ListFilterEmptyState';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import VirtualizedCardGrid from 'src/common/components/virtualizedGrid/VirtualizedCardGrid';
import { getAxiosErrorMessage } from 'src/helpers/axiosError';
import {
  collectEditionYears,
  filterMergeCandidates,
  isMergeContactMatch,
  pickSurvivorPreview,
} from 'src/helpers/participantMerge';
import useParticipantsStore from 'src/stores/participantsStore';

import MergeCandidateCard from './components/MergeCandidateCard';

export default function MergeParticipants() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const mergeCandidates = useParticipantsStore(s => s.mergeCandidates);
  const isLoadingMergeCandidates = useParticipantsStore(s => s.isLoadingMergeCandidates);
  const isMergingParticipants = useParticipantsStore(s => s.isMergingParticipants);
  const mergeParticipants = useParticipantsStore(s => s.mergeParticipants);

  const [query, setQuery] = useState('');
  const [likelyDuplicatesOnly, setLikelyDuplicatesOnly] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const filtered = useMemo(
    () =>
      filterMergeCandidates(mergeCandidates, {
        query,
        likelyDuplicatesOnly,
      }),
    [mergeCandidates, query, likelyDuplicatesOnly],
  );

  const selectedCandidates = useMemo(
    () => mergeCandidates.filter(c => selectedIds.has(c.id)),
    [mergeCandidates, selectedIds],
  );

  const toggle = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMerge = async () => {
    if (selectedCandidates.length < 2) return;

    const survivor = pickSurvivorPreview(selectedCandidates);
    if (!survivor) return;

    const absorbed = selectedCandidates.filter(c => c.id !== survivor.id);
    const years = collectEditionYears(selectedCandidates);
    const survivorName = `${survivor.lastName} ${survivor.firstName}`;
    const absorbedNames = absorbed.map(c => `${c.lastName} ${c.firstName}`).join(', ');

    const { confirmed } = await confirm({
      title: t('participants.merge.confirmTitle', { count: selectedCandidates.length }),
      description: t('participants.merge.confirmDescription', {
        survivor: survivorName,
        absorbed: absorbedNames,
        years: years.length ? years.join(', ') : t('participants.merge.noEditions'),
      }),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) return;

    try {
      const result = await mergeParticipants([...selectedIds]);
      enqueueSnackbar(
        t('participants.merge.success', {
          name: `${result.survivor.lastName} ${result.survivor.firstName}`,
          count: result.absorbedIds.length,
        }),
        { variant: 'success' },
      );
      setSelectedIds(new Set());
    } catch (error) {
      const errorCode = isAxiosError(error)
        ? (error.response?.data as { errorCode?: string } | undefined)?.errorCode
        : undefined;

      enqueueSnackbar(
        errorCode
          ? t(`participants.merge.errors.${errorCode}`)
          : getAxiosErrorMessage(error, t('participants.merge.errors.mergeFailed')),
        { variant: 'error' },
      );
    }
  };

  return (
    <DashboardListPageLayout
      title={t('participants.merge.pageTitle')}
      isLoading={isLoadingMergeCandidates}
      toolbarAsideVariant="full"
      headerActions={
        <Button
          {...dashboardPrimaryActionButtonProps()}
          startIcon={<CallMergeIcon />}
          disabled={selectedIds.size < 2 || isMergingParticipants}
          onClick={() => void handleMerge()}
        >
          {t('participants.merge.mergeAction', { count: selectedIds.size })}
        </Button>
      }
      toolbar={
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          <TextField
            size="small"
            label={t('participants.merge.search')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            sx={{ minWidth: 220, flex: '1 1 220px' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={likelyDuplicatesOnly}
                onChange={e => setLikelyDuplicatesOnly(e.target.checked)}
              />
            }
            label={t('participants.merge.likelyDuplicatesOnly')}
          />
        </Box>
      }
    >
      {filtered.length === 0 ? (
        <ListFilterEmptyState />
      ) : (
        <VirtualizedCardGrid
          items={filtered}
          getItemKey={c => c.id}
          estimateRowHeight={140}
          chrome="standard"
          renderItem={candidate => (
            <MergeCandidateCard
              candidate={candidate}
              selected={selectedIds.has(candidate.id)}
              contactMatch={isMergeContactMatch(candidate, selectedCandidates)}
              onToggle={toggle}
            />
          )}
        />
      )}
    </DashboardListPageLayout>
  );
}
